import { Publication } from '@shared/types/publication';
import { Magazine } from '@shared/types/magazine';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import IconImages from '@shared/assets/images/icons';
import { PUBLICATION_TYPE } from '@shared/types/enums';
import { ROUTES } from '@shared/configs/constants';
import { calculateTaxIncludedPrice } from '@shared/utils/utils';
import { flowResult } from 'mobx';
import { useStore } from '@shared/hooks/useStore';
import defaultCover from '@shared/assets/images/default-cover.png';
import { formatPublicationTitle } from '@shared/utils/publication';
import { showConfirmReadModal } from '@user/components/publication/ConfirmReadModal';

type QuickViewData = Publication | Magazine;

interface QuickViewModalContentProps {
    data: QuickViewData,
    onClose: () => void
}

const QuickViewModalContent = ({ data, onClose }: QuickViewModalContentProps) => {
    const navigate = useNavigate();
    const { authStore, cartStore, modalStore, publicationStore, readerStore } = useStore();
    const { isLoggedIn, profile, isStdIndividual } = authStore;

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('ja-JP').format(price);
    };

    const isPublication = 'type' in data;
    const isMagazine = !isPublication;

    const handleViewDetail = () => {
        onClose();
        if (isMagazine) {
            navigate(ROUTES.magazineDetail.href.replace(':id', data.id.toString()));
        } else {
            navigate(ROUTES.publicationDetail.href.replace(':id', data.id.toString()));
        }
    };

    if (isMagazine) {
        const magazine = data as Magazine;
        return (
            <div className='bg-white rounded-none lg:rounded flex flex-col md:flex-row items-center md:items-start gap-6 lg:gap-8 py-8 px-6 lg:px-12 h-full md:h-auto max-h-full md:max-h-none overflow-y-auto md:overflow-visible'>
                {/* Image */}
                <div className='flex-shrink-0 w-[180px] md:w-[240px] lg:w-[337px] aspect-[3/4] mx-auto md:mx-0'>
                    <img
                        src={magazine.linkImg}
                        alt={magazine.title}
                        className='w-full h-full object-contain md:object-cover border border-white'
                        onError={(e) => {
                            e.currentTarget.src = defaultCover;
                        }}
                    />
                </div>

                {/* Info */}
                <div className='flex-1 flex flex-col gap-6'>
                    {/* Title */}
                    <h2 className='text-[29px] font-semibold leading-[36px] line-clamp-2'>
                        {magazine.title}
                    </h2>

                    <div className='flex gap-[52px]'>
                        <div className='flex flex-col gap-2 items-center'>
                            <span className='text-[#999999] font-normal leading-[29px]'>巻数</span>
                            <span className='font-normal leading-[29px]'>
                                {magazine.totalMagazineSeries}
                            </span>
                        </div>
                        <div className='flex flex-col gap-2 items-center'>
                            <span className='text-[#999999] font-normal leading-[29px]'>号数</span>
                            <span className='font-normal leading-[29px]'>
                                {magazine.totalMagazineIssue}
                            </span>
                        </div>
                    </div>

                    <div className='h-[1px] w-full bg-[#E5E5E5]' />

                    {/* Description */}
                    <p className='leading-[29px] line-clamp-4'>
                        {magazine.description}
                    </p>

                    {/* View Detail Link */}
                    <button
                        onClick={handleViewDetail}
                        className='inline-flex items-center gap-1 font-normal text-[#0581B2] text-[20px] hover:underline mt-auto w-fit'
                    >
                        詳細を見る
                        <ArrowRight size={28} />
                    </button>
                </div>
            </div>
        );
    }

    const publication = data as Publication;

    const shouldShowPurchaseButtons = () => {
        return !isLoggedIn || (isStdIndividual && !(publication?.owned || publication?.isPurchase));
    };

    const shouldShowPrice = () => {
        return !isLoggedIn || isStdIndividual;
    };

    const shouldShowPreviewButton = () => {
        if (publication && publication.owned) return false;
        return true;
    };

    const shouldShowReadAllButton = () => {
        if (publication && publication.owned) return true;
        const pkg = profile?.package;
        const packageSelected = profile?.packageSelected;
        if (pkg && new Date(pkg.startDate) <= new Date() && new Date(pkg.expirationDate) > new Date() && packageSelected?.remainSelected && packageSelected.remainSelected > 0) {
            return true;
        }
        return false;
    };

    const handleBuyNow = async () => {
        if (!isLoggedIn) {
            modalStore.showLoginModal();
            return;
        }
        const res = await flowResult(cartStore.getPreCheckoutCart([publication.id.toString()]));
        if (res) {
            onClose();
            navigate(ROUTES.checkout.href, { state: { cartItems: res } });
        }
    };

    const handleAddToCart = async () => {
        if (!isLoggedIn) {
            modalStore.showLoginModal();
            return;
        }
        await flowResult(cartStore.addToCart(Number(publication.id)));
    };

    const handlePreview = () => {
        if (!isLoggedIn) {
            modalStore.showLoginModal();
            return;
        }
        readerStore.recordRecentlyViewed(publication.id);
        window.open(`/preview/${publication.id}`, 'jiho_preview');
        onClose();
    };

    const handleReadAll = () => {
        const readAll = (externalWindow?: Window | null) => {
            readerStore.recordRecentlyViewed(publication.id);
            readerStore.recordReading(publication.id);
            const url = `/reader/${publication.id}`;
            if (externalWindow) {
                externalWindow.location.href = url;
            } else {
                window.open(url, 'jiho_reader');
            }
            onClose();
        };

        if (publication.owned) {
            readAll();
            return;
        }

        showConfirmReadModal(modalStore, async () => {
            const newWindow = window.open('about:blank', 'jiho_reader');
            try {
                await flowResult(publicationStore.selectPublication(publication.id));
                await flowResult(authStore.getProfile());
                readAll(newWindow);
            } catch (error) {
                newWindow?.close();
            }
        });
    };

    return (
        <div className='bg-white rounded-none lg:rounded flex flex-col md:flex-row items-center md:items-start gap-6 lg:gap-8 py-8 px-6 lg:px-12 h-full md:h-auto max-h-full md:max-h-none overflow-y-auto md:overflow-visible'>
            {/* Publication Image */}
            <div className='flex-shrink-0 w-[180px] md:w-[240px] lg:w-[337px] mx-auto md:mx-0'>
                <img
                    src={publication.linkImg}
                    alt={publication.title}
                    className='w-full h-auto border border-white'
                    onError={(e) => {
                        e.currentTarget.src = defaultCover;
                    }}
                />
            </div>

            {/* Publication Info */}
            <div className='flex-1 flex flex-col gap-4'>
                {/* Title */}
                <h2 className='text-[29px] font-semibold leading-[36px] line-clamp-2'>
                    {formatPublicationTitle(publication)}
                </h2>

                {/* Author */}
                {publication.type === PUBLICATION_TYPE.Book && (
                    <p className='line-clamp-1'>
                        著者：
                        {publication.author || '不明'}
                    </p>
                )}

                {/* Price */}
                {shouldShowPrice() && (
                    <div className='flex items-baseline'>
                        <span className='text-[22px] font-bold text-danger'>
                            ¥
                            {formatPrice(calculateTaxIncludedPrice(publication.price))}
                        </span>
                        <span className='text-xs leading-[22px]'>（税込）</span>
                    </div>
                )}

                {/* Action Buttons */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>

                    {shouldShowPurchaseButtons() && (
                        <>
                            <button
                                onClick={handleBuyNow}
                                className='w-full flex items-center justify-center gap-1 px-8 py-4 bg-primary text-white font-semibold rounded-[10px] hover:bg-primary-hover'
                            >
                                <img src={IconImages.booksIc} alt='books' />
                                今すぐ買う
                            </button>
                            <button
                                onClick={handleAddToCart}
                                className='w-full flex items-center justify-center gap-1 px-8 py-4 bg-primary-light text-white font-semibold rounded-[10px] hover:bg-primary-hover'
                            >
                                <img src={IconImages.shoppingCartIc} alt='shopping-cart' />
                                カートへ追加
                            </button>
                        </>
                    )}
                    {
                        shouldShowPreviewButton() && (
                            <button
                                onClick={handlePreview}
                                className='w-full flex items-center justify-center gap-1 px-8 py-4 border border-primary-light text-primary-light font-semibold rounded-[10px]'
                            >
                                <img src={IconImages.eyeIc} alt='eye' className='-mb-[2px]' />
                                試し読み
                            </button>
                        )
                    }
                    {
                        shouldShowReadAllButton() && (
                            <button
                                onClick={handleReadAll}
                                className='w-full flex items-center justify-center gap-1 px-8 py-4 border border-primary-light text-primary-light font-semibold rounded-[10px]'
                            >
                                <img src={IconImages.bookOpenIc} alt='book-open' />
                                今すぐ読む
                            </button>
                        )
                    }
                </div>

                {/* Description */}
                <p className='leading-[29px] line-clamp-4'>
                    {publication.description}
                </p>

                {/* View Detail Link */}
                <button
                    onClick={handleViewDetail}
                    className='inline-flex items-center gap-1 font-normal text-[#0581B2] text-[20px] hover:underline mt-auto w-fit'
                >
                    詳細を見る
                    <ArrowRight size={24} />
                </button>
            </div>
        </div>
    );
};

export const showQuickViewModal = (
    data: QuickViewData,
    modalStore: any
) => {
    const modalId = `quick-view-${data.id}`;

    modalStore.showModal({
        id: modalId,
        size: '902px',
        showCloseIcon: true,
        closeWhenOutside: true,
        showHeader: false,
        footer: null,
        contentClassName: 'shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] w-full h-full md:w-[95vw] lg:w-full md:h-auto md:max-h-[90vh] !p-0 rounded-none md:rounded-[10px] bg-white outline-none overflow-hidden text-break',
        content: (
            <QuickViewModalContent
                data={data}
                onClose={() => modalStore.hideModal(modalId)}
            />
        )
    });
};

export default QuickViewModalContent;
